import React from "react";
import { html } from 'omni-ui';

// AG Grid imports
import { AgGridReact } from "ag-grid-react";
import {
    AllCommunityModule, 
    ModuleRegistry, 
    ClientSideRowModelModule, 
    ValidationModule,
    NumberEditorModule, 
    DateFilterModule, 
    NumberFilterModule, 
    TextFilterModule,
    CustomFilterModule, 
    CsvExportModule, 
    RowSelectionModule
} from "ag-grid-community";
import {
    TreeDataModule, 
    ColumnsToolPanelModule, 
    FiltersToolPanelModule, 
    PivotModule,
    SetFilterModule, 
    StatusBarModule, 
    ExcelExportModule, 
    LicenseManager
} from "ag-grid-enterprise";

// Shared utilities
import { 
    MediaOpsService, 
    AlertMessagesService, 
    Utils,
    AGGRID_LICENCE_KEY, 
    initAmplitude, 
    trackEvent 
} from "../../../../shared";

ModuleRegistry.registerModules([
    TreeDataModule,
    AllCommunityModule,
    ValidationModule,
    NumberEditorModule,
    ColumnsToolPanelModule,
    FiltersToolPanelModule,
    PivotModule,
    SetFilterModule,
    ClientSideRowModelModule,
    DateFilterModule,
    NumberFilterModule,
    TextFilterModule,
    StatusBarModule,
    CustomFilterModule,
    CsvExportModule,
    ExcelExportModule,
    RowSelectionModule
]);

LicenseManager.setLicenseKey(AGGRID_LICENCE_KEY);
export class MergeCampaigns extends React.Component {

    constructor(props) {
        super(props);
        this.state = this.defaultState;
        this.parentGridRef = React.createRef();
        this.unmapTableRef = React.createRef();
    }

    // Default state for the component
    defaultState = {
        parentCampaign: {},
        unMapCampaign: [],
        selectedUnmapCampaign: [],
        enableMerge: false,
    }

    /**
     * React lifecycle method called after the component is mounted.
     * Initializes state variables based on the parentCampaign and unMapCampaign props.
     */
    componentDidMount() {
        this.setState({ parentCampaign: this.props.parentCampaign, unMapCampaign: this.props.unMapCampaign });
    }

    /**
     * Handles merging of selected campaigns into the parent campaign.
     * Prepares the payload and calls the merge API.
     * Shows success or error messages based on the API response.
     */
    mergeCampaigns = () => {
        this.props.setSpinnerCount(1);
        const payload = {
            "parent_id": this.state?.parentCampaign?.synthetic_id,
            "amd_campaignid": this.state?.parentCampaign?.id,
            "mediaocean_id": this.state?.parentCampaign?.mediaocean_id,
            "export_status": this.state?.parentCampaign?.export_status,
            "campaign_taxonomy": this.state?.parentCampaign?.campaignTaxonomy,
            "campaign_taxonomy_display": this.state?.parentCampaign?.campaign_taxonomy_display,
            "taxonomy_schema": this.state?.parentCampaign?.taxonomy_schema,
            "updated_at": MediaOpsService.getTodaysDate(),
            "updated_by": this.props?.omniProfile?.email,
            "client_code": this.state?.parentCampaign?.client_code,
            "start_date": this.state?.parentCampaign?.start_date.split(" ")[0],
            "end_date": this.state?.parentCampaign?.end_date.split(" ")[0]
        }

        if (!this.state?.parentCampaign?.synthetic_id) {
            payload["created_at"] = MediaOpsService.getTodaysDate();
            payload["created_by"] = this.props?.omniProfile?.email;
        }

        const children = []
        this.selectedUnmapCampaign.forEach((campaign, index) => {
            const childObj = {};
            childObj['campaign_id'] = campaign?.synthetic_id;
            childObj['mediaocean_id'] = campaign?.mediaocean_id;
            childObj['channels'] = campaign?.media_attributes?.channels?.map(channel => channel.channel_name).toString();
            childObj['start_date'] = campaign?.start_date.split("T")[0];
            childObj['end_date'] = campaign?.end_date.split("T")[0];
            children.push(childObj);
        });
        payload['children'] = children;
        let campaignId = encodeURIComponent(this.state?.parentCampaign?.id);
        MediaOpsService.mergeCampaigns(campaignId, payload).then((res) => {
            AlertMessagesService.showSuccessToastMessage(`${this.selectedUnmapCampaign?.length} campaign(s) successfully added to ${this.state?.parentCampaign?.campaignName}.`);
            this.props.resetStateAndUpdateCampaigns()
            this.props.onCancel();
            this.setState(this.defaultState);
            trackEvent('Campaigns are merged.', { access_from: 'Campaign Listing Page' });
        }).catch((error) => {
            console.log(error);
            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.mergeCampaigns, title: 'Unable to add the campaigns', bodyMsgArr: [html`<p>${error?.response?.data}</p>`] }]);
            trackEvent('Campaigns are not merged.', { access_from: 'Campaign Listing Page', event: { error: error?.response?.data } });
        }).finally(() => {
            this.props.setSpinnerCount(0);
        });
    }

    /**
     * Handles the cancel action for the merge operation.
     * Resets the component's state and invokes the onCancel callback prop.
     */
    onCancel = () => {
        this.setState(this.defaultState);
        this.props.onCancel();
    }

    // Default column definitions for the parent campaign grid
    parentDefaultCol = {
        autoHeight: true,
        suppressHeaderMenuButton: true,
    }

    // Default column definitions for the child campaign grid
    childDefaultCol = {
        flex: 1,
        filter: true,
        wrapText: true,
        floatingFilter: true,
        autoHeight: true,
        suppressHeaderMenuButton: true,
    };

    // Filter parameters for the channel column in the merge table
    // This formatter converts the channel names to lowercase for filtering
    // and handles cases where the value is an array or a single string.
    channelFilterParams = {
        textFormatter : (value) => {
            if(Array.isArray(value)){
                return value[0]?.channel_name?.toLowerCase();
            }
            return value?.toLowerCase();
        }
    }

    // Default column definitions for the merge table
    mergeTableColumnDefs = [
        {
            headerName: "CAMPAIGN",
            field: "campaignName",
            filter: "agTextColumnFilter"
        },
        {
            headerName: "SOURCE",
            field: "source",
            filter: "agTextColumnFilter",
        },
        {
            headerName: "START DATE",
            field: "startDate",
            filter: "agTextColumnFilter",
            valueFormatter: (params) => {
                if (!params.value) {
                    return "";
                }

                const date = new Date(params.value);
                const month = date.getMonth() + 1;
                const day = date.getDate();
                return `${date.getFullYear()}-${month < 10 ? "0" + month : month}-${day < 10 ? "0" + day : day}`;
            },
        },
        {
            headerName: "END DATE",
            field: "endDate",
            filter: "agTextColumnFilter",
            valueFormatter: (params) => {
                if (!params.value) {
                    return "";
                }

                const date = new Date(params.value);
                const month = date.getMonth() + 1;
                const day = date.getDate();
                return `${date.getFullYear()}-${month < 10 ? "0" + month : month}-${day < 10 ? "0" + day : day}`;
            },
        },
        {
            headerName: "CHANNELS",
            field: "channels",
            filter: "agTextColumnFilter",
            valueFormatter: (params) => {
                if (!params.value) {
                    return "";
                }
                return MediaOpsService.getChannels(params.value);
            },
            filterParams: this.channelFilterParams,
        },
        {
            headerName: "MOH CLIENT CODE",
            field: "client_code",
            filter: "agTextColumnFilter",
        },
        {
            headerName: "PRODUCT CODE",
            field: "productid",
            filter: "agTextColumnFilter",
        },
        {
            headerName: "ESTIMATE",
            field: "estimateid",
            filter: "agTextColumnFilter",
        },
        {
            headerName: "NET MEDIA",
            field: "estimateid",
            filter: "agTextColumnFilter",
            valueFormatter: (params) => {
                if (!params) {
                    return "";
                }
                return this.props?.systemParametersData?.data?.isLocal ?
                    params.data?.local_currency_name && Utils.getLocalCurrency(params.data?.local_currency_name, params.data?.campaign_total_budget) :
                    Utils.getLocalCurrency('USD', params.data?.campaign_total_budget);
            }
        },
        {
            headerName: "MEDIAOCEAN ID",
            field: "mediaocean_id",
            filter: "agTextColumnFilter",
        },
    ];

    /**
     * Returns the column definitions for the parent campaign grid, removing filter properties.
     * @returns {Array} Array of column definition objects for the parent campaign grid.
     */
    getParentHeaders = () => {
        return this.mergeTableColumnDefs.map((colDef) => {
            let parentColDef = { ...colDef };
            delete parentColDef.filter;
            return parentColDef;
        });
    }

    // Auto size strategy for the grids
    autoSizeStrategy = {
        type: "fitCellContents",
        defaultMinWidth: 150
    }

    /**
     * Handles selection changes in the child campaign grid.
     * Updates the selectedUnmapCampaign array and enables/disables the merge button.
     * @param {object} event - AG Grid selection event.
     */
    onSelectionChanged = (event) => {
        const selectedNodes = event.api.getSelectedNodes();
        this.selectedUnmapCampaign = [];
        selectedNodes.forEach((node) => {
            this.selectedUnmapCampaign.push(node.data);
        });
        this.setState({ enableMerge: this.selectedUnmapCampaign.length > 0 });
    }

    render() {
        return (
            <>
                <div className="mergecampaign-container modal is-active">
                    <div className="mc-content">
                        <div className="header-container">
                            <span className="title">Campaign Mapping</span>
                        </div>

                        <hr></hr>

                        <div className="parent-campaign-container">
                            <p className="select-child-campaign-title">Selected Parent Campaign</p>
                            <div>
                                <AgGridReact
                                    key={'parentCampaignTable'}
                                    ref={this.parentGridRef}
                                    columnDefs={this.getParentHeaders()}
                                    rowData={[this.state.parentCampaign]}
                                    getRowId={(params) => params?.data?.id?.toString()}
                                    defaultColDef={this.parentDefaultCol}
                                    autoSizeStrategy={this.autoSizeStrategy}
                                    domLayout="autoHeight"
                                />
                            </div>
                        </div>

                        <div className="child-campaign-container">
                            <p className="select-child-campaign-title">Select campaigns to add to "{this.state.parentCampaign?.campaignName}"</p>
                            <div style={{ width: "100%", height: '90%' }}>
                                <AgGridReact
                                    key={'unmapCampaignTable'}
                                    ref={this.unmapTableRef}
                                    columnDefs={this.mergeTableColumnDefs}
                                    rowData={this.state.unMapCampaign}
                                    rowSelection={{ mode: "multiRow" }}
                                    onSelectionChanged={this.onSelectionChanged}
                                    getRowId={(params) => params?.data?.id?.toString()}
                                    defaultColDef={this.childDefaultCol}
                                    autoSizeStrategy={this.autoSizeStrategy}
                                    domLayout="normal"
                                />
                            </div>

                        </div>

                        <div className="footer-container">
                            <button className="medium" disabled={!this.state.enableMerge} onClick={this.mergeCampaigns}>Add to Campaign</button>
                            <button className="medium tertiary marge-cancel-btn" onClick={this.onCancel}>Cancel</button>
                        </div>

                    </div>
                </div>
            </>
        );
    }

}